import { useCallback, useContext, useMemo, useState } from 'react'

import { useFlag } from '@unleash/proxy-client-react'

import { ServiceContext } from '~/contexts/Service'
import { UserContext } from '~/contexts/User'
import { aiAssistantEnabled } from '~/modules/permissions'
import { persistLocally } from '~/utils'

interface FeedbackConfig {
  bodyText: string
  handleAccept: () => void
  handleDecline: () => void
  triggerFeedbackRequestModal: (callback?: () => void) => void
}

interface FeedbackConfigs {
  [key: string]: FeedbackConfig
}

interface TriggerConfig {
  triggerInterval: number
  minutesToBlock?: number
  flagKey?: string
  retrieve: (key: string) => string | null
  persist: (key: string, value: string) => void
}

export const useFeedbackRequest = () => {
  const postFeedback = useFlag('user_rating_feedback')
  const aiFeedback = useFlag('ai_feedback')
  const { selectedServiceFeatures } = useContext(ServiceContext)
  const aiAssistFlag = aiAssistantEnabled(selectedServiceFeatures)
  const item = useMemo(() => {
    if (postFeedback) return 'POST_FEEDBACK'
    if (aiFeedback && aiAssistFlag) return 'AI_FEEDBACK'
    return null
  }, [postFeedback, aiFeedback, aiAssistFlag])
  const [isFeedbackRequestModal, setIsFeedbackRequestModal] = useState(false)
  const user = useContext(UserContext)
  const userFkey = user.state?.fkey

  const closeFeedbackRequestModal = () => setIsFeedbackRequestModal(false)

  const showModal = useCallback(
    (config: TriggerConfig): ((callback?: () => void) => void) =>
      (callback?: () => void) => {
        if (!userFkey) {
          callback?.()
          return
        }

        // flag check
        if (config.flagKey !== undefined) {
          const flagValue = { true: '1', false: '0' }
          const isConditionMet = localStorage.getItem(config.flagKey) === flagValue.true
          // Reset the flag
          localStorage.setItem(config.flagKey, flagValue.false)
          // If the condition is not met, return early
          if (!isConditionMet) {
            callback?.()
            return
          }
        }

        // block trigger for x minutes
        if (config.minutesToBlock !== undefined) {
          const xMinutesKey = `feedback_last_called_${userFkey}`
          const feedbackLastCalled = localStorage.getItem(xMinutesKey)
          const now = new Date()
          if (feedbackLastCalled) {
            const lastCalled = new Date(feedbackLastCalled)
            const timeDiff = now.getTime() - lastCalled.getTime()
            const minutesDiff = timeDiff / (60 * 1000)
            if (minutesDiff < config.minutesToBlock) {
              callback?.()
              return
            }
          }
          // Set last called date to now if the condition is met
          localStorage.setItem(xMinutesKey, now.toISOString())
        }

        const storedValue = config.retrieve(userFkey) || '0'
        // Increment item count
        const currentItemCount = parseInt(storedValue, 10)
        const newItemCount = isNaN(currentItemCount) ? 1 : currentItemCount + 1
        // Update storage
        config.persist(userFkey, newItemCount.toString())
        // Check trigger condition
        if (newItemCount % config.triggerInterval === 0) setIsFeedbackRequestModal(true)
        else callback?.()
      },
    [userFkey]
  )

  const { bodyText, handleAccept, handleDecline, triggerFeedbackRequestModal } = useMemo(() => {
    const defaultConfig: FeedbackConfig = {
      bodyText: '',
      handleAccept: closeFeedbackRequestModal,
      handleDecline: closeFeedbackRequestModal,
      triggerFeedbackRequestModal: closeFeedbackRequestModal,
    }

    if (!item) return defaultConfig

    const persistKey = `${item}_COUNT`

    const configMap: FeedbackConfigs = {
      POST_FEEDBACK: {
        ...defaultConfig,
        bodyText: 'Would you like to rate your experience with Posts?',
        handleAccept: () => {
          window.open('https://forms.office.com/e/tYTNKnNCtr', '_blank', 'noopener')
          closeFeedbackRequestModal()
        },
        triggerFeedbackRequestModal: showModal({
          triggerInterval: 10,
          flagKey: 'isPostPublished',
          ...persistLocally(persistKey),
        }),
      },
      AI_FEEDBACK: {
        ...defaultConfig,
        bodyText: 'Would you like to rate your experience with Assistant?',
        handleAccept: () => {
          window.open('https://forms.office.com/e/vk7Qb5TrzG', '_blank', 'noopener')
          closeFeedbackRequestModal()
        },
        triggerFeedbackRequestModal: showModal({
          triggerInterval: 1,
          minutesToBlock: 240, // 4 hours = 240 minutes
          flagKey: 'isAssistClicked',
          ...persistLocally(persistKey),
        }),
      },
    }

    return configMap[item] || defaultConfig
  }, [item, showModal])

  return {
    closeFeedbackRequestModal,
    isFeedbackRequestModal,
    triggerFeedbackRequestModal,
    feedbackBodyText: bodyText,
    handleFeedbackAccept: handleAccept,
    handleFeedbackDecline: handleDecline,
  }
}
